// src/pages/Opportunities.jsx
//
// The idea backlog from claire_architecture.md's "Proactive Opportunity
// Generation" -- reviewed daily per Bradley. Two states an idea lands in:
// 'needs_input' (blocked on a question only Bradley can answer) and
// 'ready' (a full proposal). Answering a needs_input item, or responding
// to a ready one (approve/explore more/reject), writes bradley_response --
// that's the feedback log the next generate-opportunities run reads as
// training signal on what's landed and why.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Browser speech-to-text -- Bradley's explicit ask 2026-07-08: "99% of the
// time I won't be typing... I need voice to text." Feature-detected; falls
// back to typing-only where unsupported (older Firefox, some Safari).
const SpeechRecognitionAPI =
  typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

const CATEGORY_COLORS = {
  prospecting: "bg-blue-100 text-blue-800",
  marketing: "bg-purple-100 text-purple-800",
  merch: "bg-amber-100 text-amber-800",
  career: "bg-green-100 text-green-800",
  other: "bg-gray-100 text-gray-800",
};

function formatDateTime(v) {
  return v ? new Date(v).toLocaleString() : "—";
}

// Provenance -- Bradley's ask 2026-07-10: "I don't know where Meredith Bare
// Homes came from." There's no dedicated source column on opportunities, but
// the two insertion paths are distinguishable from what's already there:
// the 'channel-intermediary' tag only ever comes from source-interior-
// designers (a dedicated daily search lane, separate from the general scan),
// a discovery_id with no that tag means generate-opportunities' own live web
// search (source-interior-designers also sets discovery_id, hence checking
// the tag first), a lead_id with neither means it's about an existing CRM
// lead, and none of the above means a general idea (marketing/merch/career)
// not tied to a specific person.
function sourceLabel(o) {
  if (o.tags?.includes("channel-intermediary")) {
    const kind = o.tags.find((t) => t === "interior-design" || t === "art-consultant");
    return `Interior designer/art consultant sourcing${kind ? ` (${kind})` : ""}`;
  }
  if (o.discovery_id) return "Claire's live web search (daily scan)";
  if (o.lead_id) return "Existing lead in CRM";
  return "Claire's daily opportunity scan";
}

const ACTION_LABELS = {
  approved: "Approve",
  exploring: "Explore more",
  rejected: "Not interested",
  answer: "Answer",
};

const ACTION_PLACEHOLDERS = {
  approved: "Why does this land? (optional — trains what gets suggested next)",
  exploring: "What's promising, or what would make it better? (optional — read back next run)",
  rejected: "Why not? (optional — leave blank to just say no without explanation)",
  answer: "Your answer…",
};

// Opening the note box is now the FIRST step for every action, not the last
// -- clicking a button used to submit instantly with no way to explain why,
// which defeated the whole point of the feedback loop (caught by Bradley
// testing it live 2026-07-08: clicking "Explore more" silently submitted
// with no chance to say why it was good). Only Reject still hard-requires
// text before Submit is enabled; Approve/Explore encourage it but don't block.
// Draft state (conversation/note/pendingAction/noteOpen) is persisted to
// localStorage per-opportunity so a refresh mid-conversation doesn't lose it
// -- surprised Bradley 2026-07-08 even though it was the documented v1
// tradeoff. Client-side only, on purpose: no backend dependency here.
function draftKey(oId) {
  return `claire-opp-draft-${oId}`;
}

function OpportunityCard({ o, leadName, onRespond }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(o.bradley_response || "");
  const [pendingAction, setPendingAction] = useState(null);
  const [busy, setBusy] = useState(false);

  // Voice conversation -- transcript is now persisted (see below), not just
  // local React state. Bradley talks, Claude either asks one clarifying
  // question or drafts a final_response into the note box for him to
  // review/edit before hitting the existing Submit. This function never
  // writes to the DB itself -- it only proposes text.
  const [conversation, setConversation] = useState([]);
  const [listening, setListening] = useState(false);
  const [converseBusy, setConverseBusy] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");

  // Push-to-talk plumbing. recognitionRef holds the live SpeechRecognition
  // instance; finalTranscriptRef accumulates confirmed text across possibly
  // several recognition "sessions" (continuous mode can still be ended
  // early by some browsers on a long silence -- shouldListenRef tells onend
  // whether that was Bradley clicking stop, or the browser doing it to us,
  // so we can transparently restart without losing what's already said).
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const shouldListenRef = useRef(false);

  // Restore any in-progress draft for this exact opportunity on mount.
  // Parent renders one OpportunityCard per o.id with key={o.id}, so this
  // effectively only runs once per card instance -- it can't leak a draft
  // from a different opportunity because the key is baked into the id.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey(o.id));
      if (raw) {
        const saved = JSON.parse(raw);
        setNoteOpen(!!saved.noteOpen);
        setNote(typeof saved.note === "string" ? saved.note : o.bradley_response || "");
        setPendingAction(saved.pendingAction ?? null);
        setConversation(Array.isArray(saved.conversation) ? saved.conversation : []);
      }
    } catch {
      // corrupted/unavailable localStorage -- fall back to defaults, not fatal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.id]);

  // Persist the draft while a note session is open. Only writes while
  // noteOpen so we're not touching localStorage for every idle card on
  // every render.
  useEffect(() => {
    if (!noteOpen) return;
    try {
      localStorage.setItem(draftKey(o.id), JSON.stringify({ noteOpen, note, pendingAction, conversation }));
    } catch {
      // e.g. private browsing / quota -- draft persistence is best-effort
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteOpen, note, pendingAction, conversation, o.id]);

  function clearDraft() {
    try {
      localStorage.removeItem(draftKey(o.id));
    } catch {
      // ignore
    }
  }

  function stopRecognition() {
    shouldListenRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // already stopped
      }
    }
    setListening(false);
    setInterimTranscript("");
  }

  // Stop any live mic session if the card unmounts (e.g. filter changes)
  // while Bradley is mid-recording.
  useEffect(() => {
    return () => {
      shouldListenRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  function openNote(action) {
    stopRecognition();
    setPendingAction(action);
    setNote(o.bradley_response || "");
    setConversation([]);
    setVoiceError("");
    setNoteOpen(true);
  }

  async function converse(spokenText) {
    if (!spokenText.trim()) return;
    setConverseBusy(true);
    setVoiceError("");
    const nextHistory = [...conversation, { role: "user", text: spokenText }];
    setConversation(nextHistory);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/converse-opportunity`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
        body: JSON.stringify({
          opportunity: { title: o.title, body: o.body, category: o.category },
          pending_action: pendingAction,
          history: conversation,
          message: spokenText,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Claire didn't respond");
      setConversation((prev) => [...prev, { role: "assistant", text: json.reply }]);
      if (json.ready_to_commit && json.final_response) {
        setNote(json.final_response);
      }
    } catch (err) {
      setVoiceError(err.message);
    } finally {
      setConverseBusy(false);
    }
  }

  // Spoken stop words -- an alternative to clicking "Stop & send" for
  // hands-free use (2026-07-09: Bradley wants to say "done"/"stop" the way
  // he does in voice conversations elsewhere, not just click a button).
  // Only matched against the tail of what was JUST finalized, not the whole
  // transcript, so an earlier "...I'm done with the old vendor" mid-thought
  // can't accidentally trigger it -- only actually trailing off with one of
  // these words does.
  const STOP_PHRASES = ["done", "stop", "that's it", "send it", "over"];

  function stripTrailingStopPhrase(text) {
    const trimmed = text.trim();
    const bare = trimmed.replace(/[.!?,]+$/, "").toLowerCase();
    for (const phrase of STOP_PHRASES) {
      if (bare === phrase || bare.endsWith(` ${phrase}`)) {
        const cut = trimmed.length - (trimmed.length - bare.length) - phrase.length;
        return { cleaned: trimmed.slice(0, Math.max(0, cut)).trim(), stopped: true };
      }
    }
    return { cleaned: trimmed, stopped: false };
  }

  // Explicit push-to-talk, replacing the old model where the browser's own
  // silence-detection decided when Bradley was done talking -- it guessed
  // wrong whenever he paused to think and cut him off mid-thought (caught
  // live 2026-07-08). Now recording only ever starts or stops on his click
  // OR on a spoken stop word (see STOP_PHRASES above); `continuous`+
  // `interimResults` let us accumulate transcript across however many
  // recognition events fire while he's talking, and we only hand anything
  // to converse() when he explicitly stops, one way or the other.
  function createRecognition() {
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onerror = (e) => {
      // "no-speech" / "aborted" fire routinely mid-pause or on our own
      // .stop() call -- not real errors, and onend (below) decides whether
      // to restart, so don't surface these or bail out of listening.
      if (e.error === "no-speech" || e.error === "aborted") return;
      shouldListenRef.current = false;
      setListening(false);
      setVoiceError(`Voice input error: ${e.error}`);
    };
    recognition.onend = () => {
      if (shouldListenRef.current) {
        // Some browsers end a "continuous" session on their own after a
        // long silence even though Bradley never clicked stop. Restart
        // transparently -- finalTranscriptRef already holds everything
        // said so far, so nothing said before the restart is lost.
        try {
          const next = createRecognition();
          recognitionRef.current = next;
          next.start();
        } catch {
          setListening(false);
        }
      } else {
        setListening(false);
      }
    };
    recognition.onresult = (e) => {
      let interim = "";
      let sawStopWord = false;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          const { cleaned, stopped } = stripTrailingStopPhrase(chunk);
          finalTranscriptRef.current = finalTranscriptRef.current
            ? `${finalTranscriptRef.current} ${cleaned}`.trim()
            : cleaned;
          if (stopped) sawStopWord = true;
        } else {
          interim += chunk;
        }
      }
      setInterimTranscript(interim);
      if (sawStopWord) stopListeningAndSend();
    };
    return recognition;
  }

  function startListening() {
    if (!SpeechRecognitionAPI) {
      setVoiceError("Voice input isn't supported in this browser -- try Chrome or Edge.");
      return;
    }
    if (listening || converseBusy) return;
    setVoiceError("");
    finalTranscriptRef.current = "";
    setInterimTranscript("");
    shouldListenRef.current = true;
    const recognition = createRecognition();
    recognitionRef.current = recognition;
    recognition.start();
  }

  function stopListeningAndSend() {
    shouldListenRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // already stopped
      }
    }
    setListening(false);
    const finalText = `${finalTranscriptRef.current} ${interimTranscript}`.trim();
    finalTranscriptRef.current = "";
    setInterimTranscript("");
    if (finalText) {
      converse(finalText);
    }
  }

  function handleMicClick() {
    if (listening) {
      stopListeningAndSend();
    } else {
      startListening();
    }
  }

  async function finalize() {
    const status = pendingAction === "answer" ? "exploring" : pendingAction;
    setBusy(true);
    await onRespond(o, status, note.trim() || null);
    setBusy(false);
    setNoteOpen(false);
    setPendingAction(null);
    clearDraft();
  }

  const isOpen = o.status === "ready" || o.status === "needs_input";

  return (
    <div className="rounded bg-white p-4 shadow">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded px-2 py-1 text-xs font-medium ${CATEGORY_COLORS[o.category] || CATEGORY_COLORS.other}`}>
          {o.category}
        </span>
        {o.status === "needs_input" && (
          <span className="rounded bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">needs your input</span>
        )}
        {leadName && (
          <Link to={`/leads/${o.lead_id}`} className="text-xs text-blue-600 underline">
            {leadName}
          </Link>
        )}
        {o.discovery_id && (
          <Link to="/discovery" className="rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 underline">
            → added to Discovery queue
          </Link>
        )}
        <span className="ml-auto text-xs text-gray-500">{formatDateTime(o.created_at)}</span>
      </div>
      <div className="mt-1 text-xs text-gray-400">Source: {sourceLabel(o)}</div>

      <h3 className="mt-2 font-semibold">{o.title}</h3>
      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{o.body}</p>

      {o.tags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {o.tags.map((t) => (
            <span key={t} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">#{t}</span>
          ))}
        </div>
      )}

      {isOpen && !noteOpen && (
        <div className="mt-3 flex flex-wrap gap-2">
          {o.status === "needs_input" ? (
            <button type="button" onClick={() => openNote("answer")} className="rounded bg-blue-500 px-3 py-1.5 text-sm text-white hover:bg-blue-600">
              Answer
            </button>
          ) : (
            <>
              <button type="button" onClick={() => openNote("approved")} className="rounded bg-green-500 px-3 py-1.5 text-sm text-white hover:bg-green-600">
                Approve
              </button>
              <button type="button" onClick={() => openNote("exploring")} className="rounded bg-purple-500 px-3 py-1.5 text-sm text-white hover:bg-purple-600">
                Explore more
              </button>
              <button type="button" onClick={() => openNote("rejected")} className="rounded bg-red-100 px-3 py-1.5 text-sm text-red-700 hover:bg-red-200">
                No (+ explain why)
              </button>
              <button type="button" onClick={() => onRespond(o, "deleted", null)} className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-200">
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {noteOpen && (
        <form
          onSubmit={(e) => { e.preventDefault(); finalize(); }}
          className="mt-3 flex flex-col gap-2 rounded border border-blue-200 bg-blue-50 p-3"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-blue-800">{ACTION_LABELS[pendingAction]}</div>
            <button
              type="button"
              onClick={handleMicClick}
              disabled={converseBusy}
              title={
                SpeechRecognitionAPI
                  ? listening
                    ? "Stop recording and send to Claire"
                    : "Talk to Claire"
                  : "Voice input not supported in this browser"
              }
              className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                listening ? "animate-pulse bg-red-500 text-white" : "bg-purple-100 text-purple-700 hover:bg-purple-200"
              } disabled:opacity-50`}
            >
              {listening ? "🔴 Stop & send" : converseBusy ? "🎤 Thinking…" : "🎤 Talk to Claire"}
            </button>
          </div>

          {listening && (
            <div className="rounded border border-red-100 bg-red-50 p-2 text-sm italic text-gray-600">
              {interimTranscript || "Listening… (say \"done\" or \"stop\", or click \"Stop & send\")"}
            </div>
          )}

          {conversation.length > 0 && (
            <div className="flex min-h-[150px] max-h-[400px] flex-col gap-1 overflow-y-auto rounded border border-purple-100 bg-purple-50 p-2 text-sm">
              {conversation.map((turn, i) => (
                <div key={i} className={turn.role === "user" ? "text-gray-800" : "text-purple-800"}>
                  <span className="font-medium">{turn.role === "user" ? "You: " : "Claire: "}</span>
                  {turn.text}
                </div>
              ))}
            </div>
          )}
          {voiceError && <div className="text-xs text-red-600">{voiceError}</div>}

          <textarea
            className="min-h-[220px] w-full resize-y rounded border p-2 text-sm"
            placeholder={ACTION_PLACEHOLDERS[pendingAction]}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoFocus
          />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="rounded bg-blue-500 px-3 py-1.5 text-sm text-white hover:bg-blue-600 disabled:opacity-50">
              {busy ? "Saving…" : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => {
                stopRecognition();
                setNoteOpen(false);
                setPendingAction(null);
                setConversation([]);
                clearDraft();
              }}
              className="rounded bg-gray-200 px-3 py-1.5 text-sm hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {!noteOpen && o.bradley_response && (
        <div className="mt-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
          <span className="font-medium">Your response:</span> {o.bradley_response}
        </div>
      )}
      {!noteOpen && o.status !== "needs_input" && o.status !== "ready" && (
        <button
          type="button"
          onClick={() => openNote(o.status)}
          className="mt-2 text-xs text-blue-600 underline"
        >
          {o.bradley_response ? "Edit note" : "Add a note"}
        </button>
      )}
    </div>
  );
}

export default function Opportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [leadNames, setLeadNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("open"); // 'open' | 'all'
  const [generating, setGenerating] = useState(false);
  // Pacing -- Bradley's explicit ask 2026-07-10: don't get bombarded with
  // everything at once (same overwhelm pattern as the 18+ opportunity
  // backlog). Oldest-first so nothing sits forever behind newer items, and
  // adjustable right here rather than a separate settings screen, per his
  // ask to be able to change these values himself.
  const [pageSize, setPageSize] = useState(10);

  async function fetchAll() {
    setLoading(true);
    const { data, error: fetchErr } = await supabase
      .from("opportunities")
      .select("*")
      .order("created_at", { ascending: false });
    if (fetchErr) {
      setError(fetchErr.message);
    } else {
      setOpportunities(data || []);
      const leadIds = [...new Set((data || []).map((o) => o.lead_id).filter(Boolean))];
      if (leadIds.length > 0) {
        const { data: leads } = await supabase.from("leads").select("id, name").in("id", leadIds);
        const map = {};
        (leads || []).forEach((l) => { map[l.id] = l.name; });
        setLeadNames(map);
      }
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchAll();
  }, []);

  // Approving/rejecting an opportunity that came from a Discovery candidate
  // cascades into the actual Discovery decision too -- otherwise the two
  // pages drift out of sync, which is exactly the bug Bradley caught
  // 2026-07-08 (approving in Discovery left the Opportunity stale). Write
  // our own response first so the RPC's coalesce-based sync sees the real
  // text, not a placeholder; approve_discovery_lead also fires the
  // first-touch draft for prospecting opportunities.
  async function respond(o, status, bradleyResponse) {
    const patch = { status, bradley_response: bradleyResponse, responded_at: new Date().toISOString() };
    const { error: updateErr } = await supabase.from("opportunities").update(patch).eq("id", o.id);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    setOpportunities((prev) => prev.map((row) => (row.id === o.id ? { ...row, ...patch } : row)));

    if (o.discovery_id) {
      const rpc = status === "approved" ? "approve_discovery_lead" : status === "rejected" ? "reject_discovery_lead" : null;
      if (rpc) {
        const { error: rpcErr } = await supabase.rpc(rpc, { p_id: o.discovery_id });
        if (rpcErr) setError(rpcErr.message);
      }
    }
  }

  async function generateNow() {
    setGenerating(true);
    setError("");
    try {
      const res = await fetch(`${FUNCTIONS_URL}/generate-opportunities`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Generation failed");
      await fetchAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  const openItems = opportunities
    .filter((o) => o.status === "ready" || o.status === "needs_input")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // oldest first -- nothing waits forever behind newer items
  const visible = filter === "all" ? opportunities : openItems.slice(0, pageSize);
  const waitingCount = filter === "open" ? Math.max(0, openItems.length - pageSize) : 0;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">Opportunities</h1>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded border p-2 text-sm">
              <option value="open">Open (needs input / ready)</option>
              <option value="all">All</option>
            </select>
            {filter === "open" && (
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded border p-2 text-sm"
                title="How many to show at once, oldest first"
              >
                <option value={5}>Show 5 at a time</option>
                <option value={10}>Show 10 at a time</option>
                <option value={20}>Show 20 at a time</option>
                <option value={9999}>Show all</option>
              </select>
            )}
            <button
              type="button"
              onClick={generateNow}
              disabled={generating}
              className="rounded bg-purple-500 px-3 py-2 text-sm text-white hover:bg-purple-600 disabled:opacity-50"
            >
              {generating ? "Generating…" : "Generate now"}
            </button>
          </div>
        </div>

        {error ? <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

        {waitingCount > 0 && (
          <div className="mb-4 rounded bg-blue-50 p-3 text-sm text-blue-800">
            Showing the oldest {visible.length} of {openItems.length} open — {waitingCount} more waiting until you clear some of these.
          </div>
        )}

        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="rounded bg-white p-6 text-center text-sm text-gray-500 shadow">
            Nothing here. Click "Generate now" or wait for the daily run.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((o) => (
              <OpportunityCard key={o.id} o={o} leadName={o.lead_id ? leadNames[o.lead_id] : null} onRespond={respond} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
